import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import apiRequest from '../../services/api';
import Preloader from '../../components/common/Preloader';
import './AdminDashboard.css'; // Reuse existing styles or create new ones if needed

const AttendanceRecord = () => {
    const location = useLocation();
    // Default filter from location state if passed, though we can use it to filter on frontend
    const defaultFilter = location.state?.filter || 'all'; 

    // Get current date in local timezone YYYY-MM-DD
    const getLocalDateString = () => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [date, setDate] = useState(getLocalDateString());
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState(defaultFilter);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');

    useEffect(() => {
        fetchRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const fetchRecords = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await apiRequest(`/admin/attendance-records?date=${date}`, {
                method: 'GET'
            });
            setRecords(data);
        } catch (err) {
            console.error('Error fetching attendance records:', err);
            setError('Failed to load attendance records.');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (e) => {
        setDate(e.target.value);
    };

    const handleSearch = (e) => {
        const val = e.target.value;
        setSearchInput(val);
        setSearchLoading(true);
        
        if (window.searchTimeout) clearTimeout(window.searchTimeout);
        window.searchTimeout = setTimeout(() => {
            setSearchQuery(val);
            setSearchLoading(false);
        }, 600); // 600ms debounce
    };

    const formatTime = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const filteredRecords = records.filter(record => {
        // Filter by tab
        let tabMatch = true;
        if (filter === 'present') tabMatch = record.status === 'present';
        if (filter === 'absent') tabMatch = record.status === 'absent';
        
        // Filter by search query
        let searchMatch = true;
        if (searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase();
            const nameMatch = record.name?.toLowerCase().includes(query);
            const idMatch = record.employeeId?.toLowerCase().includes(query) || record._id.toLowerCase().includes(query);
            searchMatch = nameMatch || idMatch;
        }

        return tabMatch && searchMatch;
    });

    const totalEmployees = records.length;
    const presentCount = records.filter(r => r.status === 'present').length;
    const absentCount = records.filter(r => r.status === 'absent').length;

    return (
        <div className="admin-dashboard" style={{ padding: '2rem' }}>
            <div className="attendance-header">
                <h1 className="dashboard-title" style={{ margin: 0 }}>Attendance Record</h1>
                <div>
                    <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Select Date:</label>
                    <input 
                        type="date" 
                        value={date} 
                        onChange={handleDateChange} 
                        style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                <div onClick={() => setFilter('all')} style={{ padding: '0.75rem 0.5rem', backgroundColor: filter === 'all' ? '#3b82f6' : '#f1f5f9', color: filter === 'all' ? 'white' : 'black', borderRadius: '8px', cursor: 'pointer', flex: 1, textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Employees ({totalEmployees})
                </div>
                <div onClick={() => setFilter('present')} style={{ padding: '0.75rem 0.5rem', backgroundColor: filter === 'present' ? '#10b981' : '#f1f5f9', color: filter === 'present' ? 'white' : 'black', borderRadius: '8px', cursor: 'pointer', flex: 1, textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Present ({presentCount})
                </div>
                <div onClick={() => setFilter('absent')} style={{ padding: '0.75rem 0.5rem', backgroundColor: filter === 'absent' ? '#ef4444' : '#f1f5f9', color: filter === 'absent' ? 'white' : 'black', borderRadius: '8px', cursor: 'pointer', flex: 1, textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Absent ({absentCount})
                </div>
            </div>

            <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
                <div style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                </div>
                <input 
                    type="text" 
                    placeholder="Search by Employee Name or ID..." 
                    value={searchInput}
                    onChange={handleSearch}
                    style={{ 
                        width: '100%', 
                        padding: '1rem 1rem 1rem 3rem', 
                        borderRadius: '12px', 
                        border: '1px solid #e2e8f0', 
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        boxSizing: 'border-box', 
                        outline: 'none',
                        fontSize: '1rem',
                        transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.2)';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                    }}
                />
            </div>

            {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

            {(loading || searchLoading) ? <Preloader isInline={true} /> : (
                <div style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={{ padding: '1rem' }}>Employee</th>
                                <th style={{ padding: '1rem' }}>Clock In</th>
                                <th style={{ padding: '1rem' }}>Clock Out</th>
                                <th style={{ padding: '1rem' }}>Employee ID</th>
                                <th style={{ padding: '1rem' }}>Designation</th>
                                <th style={{ padding: '1rem' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRecords.map((record) => (
                                <tr key={record._id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                    <td style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden', backgroundColor: '#e2e8f0' }}>
                                            {record.profilePhoto ? (
                                                <img src={record.profilePhoto} alt={record.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '20px' }}>👤</div>
                                            )}
                                        </div>
                                        <span style={{ fontWeight: '500' }}>{record.name}</span>
                                    </td>
                                    <td style={{ padding: '1rem', fontWeight: record.clockIn ? 'bold' : 'normal', color: record.clockIn ? '#10b981' : '#94a3b8' }}>
                                        {formatTime(record.clockIn)}
                                    </td>
                                    <td style={{ padding: '1rem', fontWeight: record.clockOut ? 'bold' : 'normal', color: record.clockOut ? '#f59e0b' : '#94a3b8' }}>
                                        {formatTime(record.clockOut)}
                                    </td>
                                    <td style={{ padding: '1rem', color: '#64748b' }}>{record.employeeId || record._id.substring(0,8).toUpperCase()}</td>
                                    <td style={{ padding: '1rem', color: '#64748b' }}>{record.designation || 'N/A'}</td>
                                    <td style={{ padding: '1rem' }}>
                                        <span style={{ 
                                            padding: '4px 8px', 
                                            borderRadius: '9999px', 
                                            fontSize: '0.75rem', 
                                            fontWeight: 'bold',
                                            backgroundColor: record.status === 'present' ? '#d1fae5' : '#fee2e2',
                                            color: record.status === 'present' ? '#059669' : '#dc2626'
                                        }}>
                                            {record.status === 'present' ? 'Present' : 'Absent'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {filteredRecords.length === 0 && (
                                <tr>
                                    <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                        No employees found for this category.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AttendanceRecord;
